import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { isDisplayableDate } from 'containers/OfferLetterTemplates/constants';
import { EntriesField } from 'containers/OfferLetterTemplates/Editor/EntriesField';
import { FieldLabel, fieldLabelStyle } from 'containers/OfferLetterTemplates/Editor/FieldLabel';
import globalMessages from 'shared/global/messages';

import { DataKeyOption, TypedInput } from './types';

/**
 * The stored answers of a boolean key's Yes/No pair; the labels beside them are
 * translated. They must equal what the template editor's condition builder
 * stores for the same pair, or a condition on a boolean key never matches.
 */
export const YES = 'true';
export const NO = 'false';

/**
 * How a checkbox or availability answer's codes join into the one string the
 * form posts. The letter splits on it to name each code
 * (`OfferLetterTemplates::AnswerText::PART_SEPARATOR`).
 */
export const PART_SEPARATOR = ', ';

export type TypedControl = 'date' | 'yes_no' | 'combobox' | 'multi_select' | 'text';

/**
 * The key types whose answer is several codes joined with `PART_SEPARATOR`: a
 * checkbox list's option values and an availability answer's slots.
 */
const CODED_LIST_KEY_TYPES = ['list', 'availability'];

const isCodedList = (input: TypedInput): boolean => CODED_LIST_KEY_TYPES.includes(input.key_type ?? '');

/**
 * Which control a data key renders as (HIR-375 change 2): the key's own type
 * decides date, boolean and a checkbox list, and the opening's options decide
 * a combobox.
 *
 * A value the control cannot display falls to a control that can: a date input
 * clears a malformed date and a radio pair shows nothing for a third answer, so
 * either would put a blank control over a field that still holds — and would
 * submit — a value. The combobox, the multi-select and the text box show
 * anything.
 */
export const typedControl = (input: TypedInput, current: string): TypedControl => {
  const hasOptions = (input.options ?? []).length > 0;

  if (input.key_type === 'date' && (current === '' || isDisplayableDate(current))) return 'date';
  if (input.key_type === 'boolean') {
    return current === '' || current === YES || current === NO ? 'yes_no' : 'combobox';
  }
  if (isCodedList(input) && hasOptions) return 'multi_select';
  return hasOptions ? 'combobox' : 'text';
};

/**
 * A checkbox answer's codes. An option's value can hold the separator
 * (`Dallas, TX`), so adjacent pieces that join into an option's value stay one
 * part, longest first.
 */
export const listParts = (options: DataKeyOption[], value: string): string[] => {
  const values = new Set(options.map(option => option.value));
  const pieces = value.split(PART_SEPARATOR).map(piece => piece.trim());
  const parts: string[] = [];
  let start = 0;
  while (start < pieces.length) {
    let end = pieces.length;
    while (end > start + 1 && !values.has(pieces.slice(start, end).join(PART_SEPARATOR))) end -= 1;
    parts.push(pieces.slice(start, end).join(PART_SEPARATOR));
    start = end;
  }
  return parts.filter(part => part !== '');
};

/**
 * A coded list's entries as the control shows them. Availability slots follow
 * the options' order: hire sends every slot in the order the letter prints
 * them, Monday first (`OfferLetterTemplates::AnswerText.availability_options`),
 * so the order is defined once. A slot the options do not name follows the
 * rest. A checkbox answer keeps its stored order.
 */
export const listEntries = (input: TypedInput, value: string): string[] => {
  const options = input.options ?? [];
  const parts = listParts(options, value);
  if (input.key_type !== 'availability') return parts;

  const rank = (part: string) => {
    const index = options.findIndex(option => option.value === part);
    return index === -1 ? options.length : index;
  };
  return [...parts].sort((a, b) => rank(a) - rank(b));
};

const useStyles = makeStyles(theme => ({
  // The radio pair keeps a real <legend>, styled as every other label here.
  legend: fieldLabelStyle(theme),
  clear: {
    marginLeft: theme.spacing(1),
  },
}));

/**
 * The generated rule line under a label, above its control. The space below
 * the control stays for validation and other feedback.
 */
export const Subtext: React.FC<{ id: string; text?: string | null }> = ({ id, text }) =>
  text ? (
    // Caption, not body: supporting text sits below the label in the hierarchy.
    <Typography id={id} variant="caption" color="textSecondary" component="p" gutterBottom>
      {text}
    </Typography>
  ) : null;

export interface TypedFieldProps {
  /** The control's anchor, as `revealFirstMissing` looks it up. */
  id: string;
  label: string;
  /** The rule line under the label; see `Subtext`. */
  subtext?: string | null;
  value: string;
  input: TypedInput;
  error?: boolean;
  /** Below the control: validation and other feedback. */
  helperText?: string;
  disabled?: boolean;
  /**
   * Whether the recruiter may empty the answer. A condition-only key may; a
   * field that merges into the document still requires a value. Only the
   * Yes/No pair needs an affordance for it — every other control empties by
   * deleting its text.
   */
  clearable?: boolean;
  onChange: (value: string) => void;
}

/**
 * A typed answer that matches an option's label, in any case, stores the
 * option's value. A typed code does not match, as it does in
 * `optionForTyped`: this runs on every keystroke, so `de` on its way to
 * `Denmark` would jump to Germany.
 */
const valueForInput = (options: DataKeyOption[], typed: string): string => {
  const needle = typed.toLowerCase();
  return options.find(option => option.label.toLowerCase() === needle)?.value ?? typed;
};

const labelForValue = (options: DataKeyOption[], value: string): string =>
  options.find(option => option.value === value)?.label ?? value;

/**
 * A stored value as the control shows it, so the lines around the control
 * name it the same way: an option's label, a boolean's Yes/No, and each code
 * of a checkbox answer. The form still posts the stored value, and the letter
 * names it the same way (`OfferLetterTemplates::AnswerText#entered`), except a
 * checkbox option whose value holds the separator: the letter splits it in two
 * and prints it as stored.
 */
export const valueLabeler =
  (intl: IntlShape, input: TypedInput) =>
  (value: string): string => {
    const options = input.options ?? [];
    if (input.key_type === 'boolean' && value === YES) return intl.formatMessage(globalMessages.yes);
    if (input.key_type === 'boolean' && value === NO) return intl.formatMessage(globalMessages.no);
    if (isCodedList(input)) {
      return listEntries(input, value).map(part => labelForValue(options, part)).join(PART_SEPARATOR);
    }
    return labelForValue(options, value);
  };

/**
 * One data key's input on the Send Offer form: a combobox over the opening's
 * options that also takes a typed value, a multi-select over a checkbox or
 * availability key's options, a date picker, a Yes/No pair, or free text.
 */
export const TypedField: React.FC<TypedFieldProps> = ({
  id,
  label,
  subtext,
  value,
  input,
  error = false,
  helperText,
  disabled = false,
  clearable = false,
  onChange,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const control = typedControl(input, value);
  const helperTextId = helperText ? `${id}-helper-text` : undefined;
  const subtextId = `${id}-subtext`;
  // The rule line describes the control, as the helper text does.
  const describedBy = [subtext ? subtextId : null, helperTextId].filter(Boolean).join(' ') || undefined;
  const yesNoOptions: DataKeyOption[] = [
    { value: YES, label: intl.formatMessage(globalMessages.yes) },
    { value: NO, label: intl.formatMessage(globalMessages.no) },
  ];

  if (control === 'yes_no') {
    const labelId = `${id}-label`;
    return (
      <Box mt={2} mb={1}>
        <FormControl component="fieldset" error={error} fullWidth id={id} disabled={disabled}>
          <FormLabel component="legend" id={labelId} className={classes.legend} focused={false}>
            {label}
          </FormLabel>
          <Subtext id={subtextId} text={subtext} />
          <Box display="flex" alignItems="center">
            <RadioGroup
              row
              aria-labelledby={labelId}
              aria-describedby={describedBy}
              name={id}
              value={value}
              onChange={event => onChange(event.target.value)}
            >
              {yesNoOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {clearable && value !== '' && (
              <Button
                size="small"
                variant="text"
                className={classes.clear}
                disabled={disabled}
                onClick={() => onChange('')}
              >
                {intl.formatMessage(globalMessages.clear)}
              </Button>
            )}
          </Box>
          {helperText && <FormHelperText id={helperTextId}>{helperText}</FormHelperText>}
        </FormControl>
      </Box>
    );
  }

  if (control === 'multi_select') {
    const options = input.options ?? [];
    return (
      <Box mt={2} mb={1}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <Subtext id={subtextId} text={subtext} />
        <EntriesField
          id={id}
          size="medium"
          fullWidth
          entries={listEntries(input, value)}
          options={options}
          describedBy={describedBy}
          error={error}
          helperText={helperText}
          disabled={disabled}
          onEntriesChange={entries => onChange(entries.join(PART_SEPARATOR))}
        />
      </Box>
    );
  }

  if (control === 'combobox') {
    const options = input.key_type === 'boolean' ? yesNoOptions : input.options ?? [];
    const typed = labelForValue(options, value).trim().toLowerCase();
    return (
      <Box mt={2} mb={1}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <Subtext id={subtextId} text={subtext} />
        <Autocomplete<string>
          id={id}
          freeSolo
          // freeSolo hides the arrow by default; the list is still there to open.
          forcePopupIcon
          fullWidth
          disabled={disabled}
          options={options.map(option => option.value)}
          getOptionLabel={option => labelForValue(options, option)}
          // Every keystroke becomes `value`, so the box always echoes the
          // selection and MUI's own filter never narrows the list. A value
          // that names an option shows the whole list; anything else filters it.
          filterOptions={values => {
            if (typed === '' || options.some(option => option.value === value)) return values;
            return values.filter(option => labelForValue(options, option).toLowerCase().includes(typed));
          }}
          value={value}
          inputValue={labelForValue(options, value)}
          onChange={(_event, next) => onChange(next ?? '')}
          onInputChange={(_event, typed, reason) => {
            // `reset` is the component echoing a selection back into the box;
            // the selection itself arrives through onChange.
            if (reason !== 'reset') onChange(valueForInput(options, typed));
          }}
          renderInput={params => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, 'aria-describedby': describedBy }}
              variant="outlined"
              error={error}
              helperText={helperText}
            />
          )}
        />
      </Box>
    );
  }

  return (
    <Box mt={2} mb={1}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Subtext id={subtextId} text={subtext} />
      <TextField
        id={id}
        fullWidth
        variant="outlined"
        type={control === 'date' ? 'date' : 'text'}
        inputProps={{ 'aria-describedby': describedBy }}
        value={value}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        error={error}
        helperText={helperText}
      />
    </Box>
  );
};
